import { useMutation, useQuery } from '@tanstack/react-query'
import api from 'lib/interceptor/axios.interceptor'
import type { IUserProfile } from 'models/userProfiles'

const updateUserProfiles = async (
	profileId: string,
	previewExperienceKey: string,
	enable: string
) =>
	api.put(
		`/api/userprofiles/${profileId}/previewexperiences/${previewExperienceKey}${enable}`
	)

const resetCredentials = async (userProfileId: string) =>
	api.post(`/api/userprofiles/${userProfileId.toLowerCase()}/resetCredentials`)

const signinSignup = async (payload: { hutk: string }) =>
	api.post(`/api/signupsignin`, payload)

const addAdmin = async (email: string, role: string) =>
	api.post(`/api/userProfiles`, { id: email, role })

const addSupplier = async (email: string, role: string) =>
	api.post(`/selling/api/suppliers`, { id: email, role })

const addSalesOps = async (email: string, role: string) =>
	api.post(`/selling/api/salesops`, { id: email, role })

const getMyUserProfiles = async () => {
	const response = await api.get('/api/userprofiles/me')
	return response.data as IUserProfile
}

const getMyTeamMembers = async (userProfileId: string) => {
	const response = await api.get(`/api/userProfiles/${userProfileId}/team`)
	return response.data as IUserProfile[]
}

export const getUserProfilesById = async (userId: string) => {
	const response = await api.get(`/api/userprofiles/${userId.toLowerCase()}`)
	return response.data as IUserProfile
}

const saveProfile = async (userId: string, data: IUserProfile) => {
	const response = await api.put(
		`/api/userprofiles/${userId.toLowerCase()}`,
		data
	)
	return response.data as IUserProfile
}

const deleteProfile = async (userId: string) => {
	const response = await api.delete(`/api/userprofiles/${userId.toLowerCase()}`)
	return response.data as IUserProfile
}

const disableProfile = async (
	userId: string,
	data: { isDisabled: boolean; disabledReason: string }
) => {
	const response = await api.put(
		`/api/userprofiles/${userId.toLowerCase()}/disable`,
		data
	)
	return response.data as IUserProfile
}

const getUserProfiles = async ({
	filter,
	top,
	skip,
	orderBy
}: {
	filter: string
	top: string
	skip: string
	orderBy: { field: string; direction: number }
}) => {
	const response = await api.get(
		`/api/userprofiles?$filter=${filter}&$offset=${skip}&$limit=${top}&$orderby=${orderBy.field}+${orderBy.direction < 0 ? 'desc' : 'asc'}`
	)
	return response.data as IUserProfile[]
}

const getUserProfilesByRawQuery = async ({
	condition,
	top,
	skip,
	orderBy
}: {
	condition: string
	top: string
	skip: string
	orderBy: string
}) => {
	const response = await api.get(
		`/api/userprofiles?$isRawQuery=true&$condition=${encodeURIComponent(condition)}&$offset=${skip}&$limit=${top}&$orderby=${orderBy}`
	)
	return response.data as IUserProfile[]
}

export const useGetMyUserProfile = (enabled = false) =>
	useQuery({
		queryKey: ['userprofiles/me'],
		queryFn: async () => getMyUserProfiles(),
		enabled
	})

export const useGetMyTeamMembers = (userProfileId: string) =>
	useQuery({
		queryKey: ['my-team', userProfileId],
		queryFn: async () => getMyTeamMembers(userProfileId)
	})

export const useGetUserProfileById = (userId: string, enabled = true) =>
	useQuery({
		queryKey: ['userprofile', userId],
		queryFn: async () => getUserProfilesById(userId),
		enabled
	})

export const useUserProfilesMutation = () =>
	useMutation({
		mutationKey: ['updateUserProfiles'],
		mutationFn: async ({
			profileId,
			enable,
			previewExperienceKey
		}: {
			profileId: string
			previewExperienceKey: string
			enable: string
		}) => updateUserProfiles(profileId, previewExperienceKey, enable)
	})

export const useResetCredentials = () =>
	useMutation({
		mutationKey: ['reset-credentials'],
		mutationFn: async (userProfileId: string) => resetCredentials(userProfileId)
	})

export const useDeleteProfile = () =>
	useMutation({
		mutationKey: ['delete-profile'],
		mutationFn: async ({ userProfileId }: { userProfileId: string }) =>
			deleteProfile(userProfileId)
	})

export const useSaveProfile = () =>
	useMutation({
		mutationKey: ['save-profile'],
		mutationFn: async ({
			userProfileId,
			data
		}: {
			userProfileId: string
			data: IUserProfile
		}) => saveProfile(userProfileId, data)
	})

export const useDisableProfile = () =>
	useMutation({
		mutationKey: ['disable-profile'],
		mutationFn: async ({
			userProfileId,
			data
		}: {
			userProfileId: string
			data: { isDisabled: boolean; disabledReason: string }
		}) => disableProfile(userProfileId, data)
	})

export const useSigninSignupMutation = () =>
	useMutation({
		mutationKey: ['signupsignin'],
		mutationFn: async (payload: { hutk: string }) => signinSignup(payload)
	})

export const useAddAdmin = () =>
	useMutation({
		mutationKey: ['addAdmin'],
		mutationFn: async ({ email, role }: { email: string; role: string }) =>
			addAdmin(email, role)
	})

export const useAddSupplier = () =>
	useMutation({
		mutationKey: ['addSupplier'],
		mutationFn: async ({ email, role }: { email: string; role: string }) =>
			addSupplier(email, role)
	})

export const useAddSalesOps = () =>
	useMutation({
		mutationKey: ['addSalesOps'],
		mutationFn: async ({ email, role }: { email: string; role: string }) =>
			addSalesOps(email, role)
	})

export const useGetUserProfiles = ({
	filter,
	top,
	skip,
	orderBy,
	enabled
}: {
	filter: string
	top: string
	skip: string
	orderBy: { field: string; direction: number }
	enabled?: boolean
}) =>
	useQuery({
		queryKey: ['userProfiles', filter, top, skip, orderBy],
		queryFn: async () => getUserProfiles({ filter, top, skip, orderBy }),
		enabled
	})

export const useGetUserProfilesByRawQuery = ({
	condition,
	top,
	skip,
	orderBy
}: {
	condition: string
	top: string
	skip: string
	orderBy: string
}) =>
	useQuery({
		queryKey: ['userProfilesRaw', condition, top, skip, orderBy],
		queryFn: async () =>
			getUserProfilesByRawQuery({ condition, top, skip, orderBy })
	})
